import React, { useState, useEffect, useCallback } from 'react';
import { paymentAPI, userAPI, optionsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Search, UserCheck, UserX, Filter, Download, ListChecks, X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import ResponsiveTable from '../components/common/ResponsiveTable';
import useDarkReady from '../theme/useDarkReady';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

const PENDING_REASONS_KEY = 'collection_pending_reasons';

// Modal for the admin to manage the reason options agents pick from when
// marking a collection as pending. Empty list = agents see the built-in defaults.
function PendingReasonsModal({ onClose }) {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReason, setNewReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    optionsAPI.get(PENDING_REASONS_KEY)
      .then(({ data }) => setReasons(data.data || []))
      .catch(() => toast.error('Failed to load reasons'))
      .finally(() => setLoading(false));
  }, []);

  const addReason = async () => {
    const value = newReason.trim();
    if (!value) return;
    setSaving(true);
    try {
      const { data } = await optionsAPI.add(PENDING_REASONS_KEY, value);
      setReasons(data.data || []);
      setNewReason('');
    } catch {
      toast.error('Failed to add reason');
    } finally {
      setSaving(false);
    }
  };

  const removeReason = async (value) => {
    try {
      const { data } = await optionsAPI.remove(PENDING_REASONS_KEY, value);
      setReasons(data.data || []);
    } catch {
      toast.error('Failed to remove reason');
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--card-bg, #fff)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 440
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7 }}>
            <ListChecks size={16} /> Pending Reasons
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <p className="text-muted" style={{ fontSize: '.76rem', marginBottom: 14 }}>
          These options appear in the agent app when a collection is marked pending.
          If the list is empty, the built-in default reasons are shown.
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            className="form-control"
            placeholder="New reason e.g. ઘરે ન હતા"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addReason()}
          />
          <button className="btn btn-primary btn-sm" onClick={addReason} disabled={saving || !newReason.trim()}>
            <Plus size={14} /> Add
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : reasons.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '18px 8px', color: 'var(--text-muted)', fontSize: '.82rem' }}>
            No custom reasons yet — agents currently see the default list.
          </div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {reasons.map((r) => (
              <div key={r} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6, fontSize: '.86rem'
              }}>
                <span>{r}</span>
                <button onClick={() => removeReason(r)} title="Remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-dc2626)', display: 'flex' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export default function AgentCollectionPage() {
  useDarkReady();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [attendanceTab, setAttendanceTab] = useState('all'); // all | present | absent
  const [loginLoading, setLoginLoading] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await paymentAPI.getAgentSummary();
      setRows(data.data || []);
    } catch {
      toast.error('Failed to load agent data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleAttendance = async (agentId) => {
    try {
      const { data } = await userAPI.toggleAttendance(agentId);
      toast.success(data.isPresent ? 'Marked Present' : 'Marked Absent');
      load();
    } catch {
      toast.error('Failed to update attendance');
    }
  };

  const handleLoginAs = async (row) => {
    setLoginLoading(row.agent._id);
    try {
      const { data } = await userAPI.loginAs(row.agent._id);
      // Save admin token before switching
      localStorage.setItem('adminToken', localStorage.getItem('token'));
      const adminRefresh = localStorage.getItem('refreshToken');
      if (adminRefresh) localStorage.setItem('adminRefreshToken', adminRefresh);
      localStorage.setItem('impersonatingName', row.agent.name);
      // Switch to agent token
      localStorage.setItem('token', data.data.token);
      localStorage.removeItem('refreshToken');
      toast.success(`Logged in as ${row.agent.name}`);
      window.location.href = row.agent.role === 'agent' ? '/agent' : '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoginLoading(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const [paymentsRes, pendingRes] = await Promise.all([
        paymentAPI.getAll({ status: 'completed', limit: 100000 }),
        paymentAPI.getCollectionPending({}),
      ]);
      const payments = paymentsRes.data.data || [];
      const pending = pendingRes.data.data || [];
      const reasonLabels = Object.fromEntries((pendingRes.data.reasons || []).map(r => [r.key, r.label]));

      const headers = ['TYPE', 'AGENT', 'CUSTOMER', 'CUSTOMER CODE', 'MOBILE', 'AMOUNT', 'REASON', 'DATE & TIME', 'NOTES'];
      const paymentRows = payments.map(p => [
        'Collection', p.collectedBy?.name || '', p.customer?.name || '', p.customer?.customerId || '', p.customer?.mobile || '',
        p.totalAmount || 0, '', new Date(p.paymentDate).toLocaleString('en-GB'), p.notes || '',
      ]);
      const pendingRows = pending.map(c => [
        'Collection Pending', c.agent?.name || '', c.customer?.name || '', c.customer?.customerId || '', c.customer?.mobile || '',
        '', reasonLabels[c.reason] || c.reason, new Date(c.recordedAt).toLocaleString('en-GB'), c.notes || '',
      ]);
      const allRows = [...paymentRows, ...pendingRows];
      const csv = [headers, ...allRows]
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent_collections_${format(new Date(), 'dd-MMM-yyyy')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export collection data');
    } finally {
      setExporting(false);
    }
  };

  const filtered = rows.filter(row => {
    const matchSearch = !search ||
      row.agent.name.toLowerCase().includes(search.toLowerCase()) ||
      row.agent.mobile?.includes(search);
    const matchTab =
      attendanceTab === 'all' ||
      (attendanceTab === 'present' && row.agent.isPresent) ||
      (attendanceTab === 'absent' && !row.agent.isPresent);
    return matchSearch && matchTab;
  });

  const columns = [
    { key: 'sno', label: 'S.No', width: 44, hideInCard: true, render: (row, i) => i + 1 },
    {
      key: 'name', label: 'Name', title: true, width: 200,
      render: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div className="avatar-circle" style={{
            width: 32, height: 32, fontSize: '.78rem', flexShrink: 0,
            background: row.agent.isPresent ? 'var(--cb-dcfce7)' : 'var(--cb-f1f5f9)',
            color: row.agent.isPresent ? 'var(--c-15803d)' : 'var(--text-muted)',
            border: row.agent.isPresent ? '2px solid var(--cb-86efac)' : '2px solid transparent',
          }}>
            {row.agent.name?.[0]?.toUpperCase()}
          </div>
          <span style={{ fontWeight: 600, fontSize: '.88rem' }}>{row.agent.name}</span>
        </div>
      ),
    },
    {
      key: 'mobile', label: 'Mobile', width: 130, half: true,
      render: row => <span style={{ fontSize: '.85rem', fontFamily: 'monospace' }}>{row.agent.mobile}</span>,
    },
    {
      key: 'monthly', label: 'Monthly Total Collection', width: 150, half: true,
      render: row => (
        <div>
          <div style={{ fontWeight: 700, color: row.monthly.total > 0 ? 'var(--primary)' : 'var(--text-muted)', fontSize: '.88rem' }}>₹ {fmt(row.monthly.total)}</div>
          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1 }}>From {row.monthly.customers} Customer</div>
        </div>
      ),
    },
    {
      key: 'today', label: "Today's Collection", width: 150, half: true,
      render: row => (
        <div>
          <div style={{ fontWeight: 700, color: row.today.total > 0 ? 'var(--primary)' : 'var(--text-muted)', fontSize: '.88rem' }}>₹ {fmt(row.today.total)}</div>
          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1 }}>From {row.today.customers} Customer</div>
        </div>
      ),
    },
    {
      key: 'action', label: 'Action',
      render: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/agent-collection/${row.agent._id}`)}>
            Detail
          </button>
          <button
            className="btn btn-sm"
            style={{ background: 'var(--primary)', color: 'var(--c-ffffff)', border: 'none' }}
            onClick={() => handleLoginAs(row)}
            disabled={loginLoading === row.agent._id}>
            {loginLoading === row.agent._id ? '...' : 'Login'}
          </button>
          <button
            onClick={() => handleToggleAttendance(row.agent._id)}
            className={`btn btn-sm ${row.agent.isPresent ? 'btn-success' : 'btn-secondary'}`}
            style={{ minWidth: 56 }}>
            {row.agent.isPresent ? <UserCheck size={11} /> : <UserX size={11} />}
            {row.agent.isPresent ? ' Active' : ' Absent'}
          </button>
        </div>
      ),
    },
  ];

  const totalAgents  = rows.length;
  const presentCount = rows.filter(r => r.agent.isPresent).length;
  const absentCount  = totalAgents - presentCount;
  const grandTotal   = rows.reduce((s, r) => s + r.allTime.total, 0);
  const monthlyTotal = rows.reduce((s, r) => s + r.monthly.total, 0);
  const todayTotal   = rows.reduce((s, r) => s + r.today.total, 0);

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex justify-between items-center mb-5" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700 }}>Agents</h2>
          <p className="text-muted text-small">Manage agents and track their collections</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => setReasonsOpen(true)} title="Manage the reason options agents see when marking a collection pending">
            <ListChecks size={15} /> Pending Reasons
          </button>
          <button className="btn btn-secondary" onClick={handleExport} disabled={exporting} title="Download Collection + Collection Pending as CSV/Excel">
            <Download size={15} /> {exporting ? 'Exporting…' : 'Export Excel/CSV'}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/users')}>
            <Plus size={15} /> Add New Agent
          </button>
        </div>
      </div>

      {/* ── Top stat cards (bix42 style) ── */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {/* Agents Allowed */}
        <div style={{ background: 'var(--cb-f1f5f9)', borderRadius: 14, padding: '20px 24px', minWidth: 140, textAlign: 'center', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '2.2rem', fontWeight: 900, color: 'var(--primary)' }}>{totalAgents}</div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: 4 }}>Agents Allowed</div>
        </div>
        {/* Agents Used */}
        <div style={{ background: 'var(--cb-f1f5f9)', borderRadius: 14, padding: '20px 24px', minWidth: 140, textAlign: 'center', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '2.2rem', fontWeight: 900, color: 'var(--primary)' }}>{totalAgents}</div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: 4 }}>Agents Used</div>
        </div>
        {/* Get Agents / Today */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, var(--primary) 100%)', borderRadius: 14, padding: '20px 24px', minWidth: 160, textAlign: 'center', color: 'var(--c-ffffff)', flex: 1 }}>
          <div style={{ fontSize: '1.6rem', fontWeight: 900 }}>₹{fmt(todayTotal)}</div>
          <div style={{ fontSize: '.78rem', opacity: .7, fontWeight: 600, marginTop: 4 }}>Today's Collection</div>
        </div>
        {/* Monthly */}
        <div style={{ background: 'var(--cb-eff6ff)', borderRadius: 14, padding: '20px 24px', minWidth: 160, textAlign: 'center', border: '1px solid var(--cb-bfdbfe)', flex: 1 }}>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--primary)' }}>₹{fmt(monthlyTotal)}</div>
          <div style={{ fontSize: '.78rem', color: 'var(--primary)', fontWeight: 600, marginTop: 4 }}>Monthly Collection</div>
        </div>
        {/* Total */}
        <div style={{ background: 'var(--cb-f0fdf4)', borderRadius: 14, padding: '20px 24px', minWidth: 160, textAlign: 'center', border: '1px solid var(--cb-bbf7d0)', flex: 1 }}>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--c-15803d)' }}>₹{fmt(grandTotal)}</div>
          <div style={{ fontSize: '.78rem', color: 'var(--c-15803d)', fontWeight: 600, marginTop: 4 }}>Total Collection</div>
        </div>
      </div>

      {/* ── Filters & Options bar (bix42 style) ── */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <Filter size={13} /> Filters And Options
        </span>
        <div className="search-input-wrapper" style={{ flex: 1, minWidth: 200 }}>
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search for..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* ── Present / Absent tabs + table ── */}
      <div className="card" style={{ marginTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 'none' }}>
        {/* Tab row */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 0 0 0' }}>
          {[
            { v: 'all',     l: 'General Details' },
            { v: 'present', l: `Present (${presentCount})` },
            { v: 'absent',  l: `Absent (${absentCount})` },
          ].map(({ v, l }) => (
            <button key={v} onClick={() => setAttendanceTab(v)}
              style={{
                padding: '11px 20px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '.83rem', fontWeight: 700,
                color: attendanceTab === v ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: attendanceTab === v ? '2.5px solid var(--primary)' : '2.5px solid transparent',
                marginBottom: -1,
              }}>
              {l}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', padding: '8px 16px', fontSize: '.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
            {filtered.length} agent{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--text-muted)' }}>
              <UserCheck size={44} style={{ marginBottom: 12, opacity: .2 }} />
              <p style={{ fontWeight: 600 }}>No agents found</p>
            </div>
          ) : (
            <ResponsiveTable
              columns={columns}
              rows={filtered}
              rowKey={row => row.agent._id}
            />
          )}
        </div>
      </div>

      {reasonsOpen && <PendingReasonsModal onClose={() => setReasonsOpen(false)} />}

    </div>
  );
}
